import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { catchError } from 'rxjs/operators';
import { Customer } from '../../../models/customer.model';
import { CustomerUser, UserInvitation, ClientRole, CustomerManagerCompany, CustomerUserAccess, CustomersUser, ExternalOrganisation } from '@model';
import { SxErrorService } from '../../../services/sx-error.service';
import { UserInfo } from 'os';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {

  constructor(private http: HttpClient, private sxErrorService: SxErrorService) {}

  getMyCustomers(): Observable<Customer[]> {
    return this.http
      .get<Customer[]>(`${environment.apiEndpoint}/customers`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMyCustomersUserAccess(): Observable<CustomerUserAccess[]> {
    return this.http
      .get<CustomerUserAccess[]>(`${environment.apiEndpoint}/customers/access`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMyCustomersUsers(): Observable<CustomerUser[]> {
    return this.http
      .get<CustomerUser[]>(`${environment.apiEndpoint}/customers/users`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMyCustomersInvitations(): Observable<UserInvitation[]> {
    return this.http
      .get<UserInvitation[]>(`${environment.apiEndpoint}/customers/invitations`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getMyCustomerUsers(customerId: string): Observable<CustomerUser[]> {
    return this.http
      .get<CustomerUser[]>(`${environment.apiEndpoint}/customers/${customerId}/users`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  getSelectedCustomersUserAccess(userId: string): Observable<CustomerUserAccess[]> {
    return this.http
      .get<CustomerUserAccess[]>(`${environment.apiEndpoint}/customers/access/${userId}`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  // One to Many
  bulkCreateCustomerInvitations(invitation: UserInvitation[]): Observable<UserInvitation[]> {
    return this.http
      .post<UserInvitation[]>(
        `${environment.apiEndpoint}/customers/${invitation[0].customerId}/invitations`,
        invitation
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  // Many to Many
  bulkCreateCustomersInvitations(invitation: UserInvitation[]): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${environment.apiEndpoint}/customers/invitations/bulkadd`,
        invitation
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getCustomerInvitations(customerId: string): Observable<UserInvitation[]> {
    return this.http
      .get<UserInvitation[]>(`${environment.apiEndpoint}/customers/${customerId}/invitations`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  updateCustomerUser(customerUser: CustomerUser): Observable<CustomerUser> {
    return this.http
      .post<CustomerUser>(
        `${environment.apiEndpoint}/customers/${customerUser.customerId}/users/${customerUser.userId}`,
        customerUser
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  addCustomerUser(customerUser: CustomerUser): Observable<CustomerUser> {
    return this.http
      .post<CustomerUser>(
        `${environment.apiEndpoint}/customers/${customerUser.customerId}/users/${customerUser.userId}/add`,
        customerUser
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  bulkUpdateCustomerUser(customerUsers: CustomersUser[]): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${environment.apiEndpoint}/customers/users/${customerUsers[0].userId}/bulkupdate`,
        customerUsers
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  removeCustomerUser(customerUser: CustomerUser): Observable<CustomerUser> {
    return this.http
      .post<CustomerUser>(
        `${environment.apiEndpoint}/customers/${customerUser.customerId}/users/${customerUser.userId}/delete`,
        customerUser)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  bulkRemoveCustomerUser(customerUsers: CustomerUser[]): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${environment.apiEndpoint}/customers/users/${customerUsers[0].userId}/bulkdelete`,
        customerUsers)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  setPrimaryCustomerUser(customerUser: CustomerUser): Observable<CustomerUser> {
    return this.http
      .post<CustomerUser>(
        `${environment.apiEndpoint}/customers/${customerUser.customerId}/SetPrimaryContact`,
        customerUser
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getMyClientRoles(customerId: string): Observable<ClientRole[]> {
    return this.http
      .get<ClientRole[]>(`${environment.apiEndpoint}/customers/${customerId}/clientroles`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  removeCustomerInvitation(invitation: UserInvitation): Observable<UserInvitation> {
    return this.http
      .delete<any>(`${environment.apiEndpoint}/customers/${invitation.customerId}/invitations/${invitation.id}`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getExternalOrganisations(): Observable<ExternalOrganisation[]> {
    return this.http
      .get<ExternalOrganisation[]>(`${environment.apiEndpoint}/customers/externalorganisations`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  sendCustomerInvitation(invitation: UserInvitation): Observable<UserInvitation> {
    return this.http
      .post<UserInvitation>(
        `${environment.apiEndpoint}/customers/${invitation.customerId}/invitations/send`,
        invitation
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  getMyCustomerManagerCompanies(customerId: string): Observable<CustomerManagerCompany[]> {
    return this.http
      .get<CustomerManagerCompany[]>(`${environment.apiEndpoint}/customers/${customerId}/ManagerCompanies`)
      .pipe(catchError((error: any) => this.sxErrorService.handleError(error)));
  }

  createCustomerManagerCompany(input: CustomerManagerCompany): Observable<CustomerManagerCompany> {
    return this.http
      .post<CustomerManagerCompany>(
        `${environment.apiEndpoint}/customers/${input.customerId}/managercompanies/add`,
        input
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  deleteCustomerManagerCompany(input: CustomerManagerCompany): Observable<CustomerManagerCompany> {
    return this.http
      .delete<any>(`${environment.apiEndpoint}/customers/${input.customerId}/managercompanies/${input.id}`)
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }

  updateCustomerManagerCompany(input: CustomerManagerCompany): Observable<CustomerManagerCompany> {
    return this.http
      .post<CustomerManagerCompany>(
        `${environment.apiEndpoint}/customers/${input.customerId}/managercompanies/${input.managerCompanyId}/update`,
        input
      )
      .pipe(catchError((error: any) => throwError(() => new Error(error.json()))));
  }
}
